import generated from './validate.generated.js';
import { SHAPE_LIMITS, SHAPE_PART_TYPES, isShapeKind, measureParts } from './shape-limits.js';
import { assessMotion } from './motion.js';
import { featureOf } from './world-data.js';
import { assessWorldFeatures } from './worlds.js';
import { CODE_WORLD_CATALOGUE, worldIn } from './world-catalogue.js';
import { assessPanos } from './panos.js';
import { assessFurnishing } from './furniture.js';
import { POSTER_FIELDS, POSTER_LIMITS, POSTER_REFUSED, isPosterKind } from './posters.js';
import { assessModels } from './models.js';
import { assessModelDistance } from './model-steps.js';
/**
 * Structural validation, compiled from the schema at build time.
 *
 * Ajv normally builds this function from a string when the module loads, which
 * a Content Security Policy without 'unsafe-eval' forbids -- and the headset
 * app runs under one (docs/06-security-compliance.md). Precompiling moves the
 * work to the build, keeps Ajv's compiler out of the headset bundle, and means
 * the API and the app run the same generated function rather than each
 * compiling their own (ADR-010).
 *
 * The cast is where the generated code meets the generated types: both come
 * from lesson.schema.json, so they are the same contract by construction, and
 * CI fails if either has drifted from it.
 */
const validateStructure = generated;
/**
 * Says what is wrong with a `type` the schema's `discriminator` refused, and
 * which types would do. A shape part's alternatives are references to
 * `shape*` definitions; the only other discriminated list is the scene's,
 * written out in place. A part type that exists and is still refused can only
 * be a group too deep down: the deepest level offers none.
 */
function wrongKind(error) {
    const parent = error.parentSchema;
    const alternatives = parent?.oneOf ?? [];
    const tag = error.params['tagValue'];
    const isPart = alternatives.some((a) => a.$ref?.startsWith('#/definitions/shape') === true);
    if (isPart) {
        const groups = alternatives.some((a) => a.$ref?.includes('Group') === true);
        const types = SHAPE_PART_TYPES.filter((type) => groups || type !== 'group');
        if (tag === undefined)
            return 'a part needs a "type"';
        if (typeof tag !== 'string')
            return "a part's type must be a word such as box";
        if (tag === 'group')
            return 'a group cannot go here: groups nest at most two deep';
        return `unknown part type "${tag}"; use one of ${types.join(', ')}`;
    }
    const types = alternatives
        .map((a) => a.properties?.type?.const)
        .filter((type) => typeof type === 'string');
    if (tag === undefined)
        return 'a scene needs a "type"';
    if (typeof tag !== 'string') {
        return `a scene's type must be a word such as ${types[0] ?? 'studio'}`;
    }
    return `unknown scene type "${tag}"; use one of ${types.join(', ')}`;
}
function fromAjv(error) {
    const path = error.instancePath === '' ? '/' : error.instancePath;
    if (error.keyword === 'additionalProperties') {
        return {
            path,
            message: `unknown property "${String(error.params['additionalProperty'])}"`,
        };
    }
    // Ajv reports a failed `not` as "must NOT be valid", which tells a lesson
    // author nothing. Ours are all mutual-exclusion rules, so name the fields.
    if (error.keyword === 'not') {
        const forbidden = error.schema?.required;
        if (forbidden?.length) {
            return { path, message: `must not have ${forbidden.join(' and ')} on the same step` };
        }
    }
    // A scene's or a shape part's `type` picks which kind it is (the schema's
    // `discriminator`), so a wrong one arrives as one error, here, rather than
    // as a failure against every kind in turn.
    if (error.keyword === 'discriminator')
        return { path, message: wrongKind(error) };
    return { path, message: error.message ?? 'is invalid' };
}
/**
 * Rules that JSON Schema cannot express: they relate one part of the document
 * to another. Referential integrity matters here because the headset app
 * resolves these names at runtime -- a hotspot pointing at an object that does
 * not exist is a lesson that silently does nothing on a child's face.
 */
function semanticErrors(lesson, motion = true, world = true, pano = true, furnish = true, models = true, catalogue, worlds, checkWorldExists = false) {
    const errors = [];
    const add = (path, message) => void errors.push({ path, message });
    // mode and scene.type describe the same thing from two directions. Expressed
    // here rather than as a top-level allOf in the schema, because an allOf makes
    // the generated TypeScript widen to an index signature and lose its keys.
    const sceneIsAr = lesson.scene.type === 'ar-room';
    if (lesson.mode === 'ar' && !sceneIsAr) {
        add('/scene/type', `mode "ar" needs scene.type "ar-room", got "${lesson.scene.type}"`);
    }
    if (lesson.mode === 'vr' && sceneIsAr) {
        add('/scene/type', 'scene.type "ar-room" needs mode "ar"');
    }
    // Knowing the worlds and requiring the world to exist are two things (ADR-050): a world the
    // catalogue does not know has no features and no placement space to judge a lesson by, so those
    // rules are skipped for it (no false refusals); that it exists is asked only when `requireKnownWorld`
    // (the save, generate and publish paths), never by the headset, which shows the studio instead.
    const unknownWorld = lesson.scene.type === 'world' && !worldIn(worlds ?? CODE_WORLD_CATALOGUE, lesson.scene.world);
    if (checkWorldExists && unknownWorld) {
        add('/scene/world', `world "${String(lesson.scene.type === 'world' ? lesson.scene.world : '')}" is not in the catalogue`);
    }
    const objectIds = new Set(Object.keys(lesson.objects ?? {}));
    const requireObject = (name, path, what) => {
        if (!objectIds.has(name))
            add(path, `${what} references unknown object "${name}"`);
    };
    // `view.look` and `focus` may name a world feature instead (wereld:<feature>),
    // judged by assessWorldFeatures below.
    const requireSubject = (name, path, what) => {
        if (featureOf(name) === undefined)
            requireObject(name, path, what);
    };
    const lessonGlow = new Set();
    for (const [id, object] of Object.entries(lesson.objects ?? {})) {
        // An object is either placed at a fixed position or orbits a parent. Kept
        // here rather than as a oneOf in the schema: a root-level oneOf makes the
        // generated TypeScript collapse to an index signature.
        const placed = object.pos !== undefined;
        const orbiting = object.parent !== undefined || object.orbit !== undefined;
        if (!placed && !orbiting) {
            add(`/objects/${id}`, `"${id}" needs either pos, or parent and orbit`);
        }
        else if (placed && orbiting) {
            add(`/objects/${id}`, `"${id}" has both pos and an orbit; it can only have one`);
        }
        else if (orbiting && (object.parent === undefined || object.orbit === undefined)) {
            add(`/objects/${id}`, `"${id}" orbits, so it needs both parent and orbit`);
        }
        if (object.parent !== undefined) {
            requireObject(object.parent, `/objects/${id}/parent`, 'parent');
            if (object.parent === id)
                add(`/objects/${id}/parent`, `"${id}" cannot orbit itself`);
        }
        // What a shape is made of is in the lesson (ADR-030): without parts it is
        // nothing, and parts on anything else would be drawn by nobody.
        if (isShapeKind(object.kind)) {
            if (object.parts === undefined) {
                add(`/objects/${id}`, `"${id}" is a shape, so it needs parts`);
            }
            else {
                // Per level the schema allows 64; across levels only a count can say.
                const { count, glowColours } = measureParts(object.parts);
                glowColours.forEach((colour) => lessonGlow.add(colour));
                if (glowColours.length > SHAPE_LIMITS.glowColoursPerShape) {
                    add(`/objects/${id}/parts`, `"${id}" glows in ${glowColours.length} colours; a shape may glow in at most ${SHAPE_LIMITS.glowColoursPerShape}`);
                }
                if (count > SHAPE_LIMITS.partsPerObject) {
                    add(`/objects/${id}/parts`, `"${id}" has ${count} parts counting those in groups; a shape may have at most ${SHAPE_LIMITS.partsPerObject}`);
                }
            }
        }
        else if (object.parts !== undefined) {
            add(`/objects/${id}/parts`, `"${id}" has parts, which only a shape: object takes`);
        }
        // A framed photo (docs/superpowers/specs/2026-09-28-posters-design.md):
        // placed by pos, facing and width only, and its fields on nothing else.
        if (isPosterKind(object.kind)) {
            if (object.width === undefined)
                add(`/objects/${id}`, `"${id}" is a poster, so it needs a width`);
            if (object.pos === undefined || orbiting) {
                add(`/objects/${id}`, `"${id}" is a poster, so it is placed by pos, not in an orbit`);
            }
            if (object.media === undefined && object.want === undefined) {
                add(`/objects/${id}`, `"${id}" is a poster, so it needs media or a want`);
            }
            if (lesson.mode !== 'vr') {
                add(`/objects/${id}`, `"${id}" is a poster, which only a vr lesson allows`);
            }
            for (const field of POSTER_REFUSED) {
                if (object[field] !== undefined) {
                    add(`/objects/${id}/${field}`, `"${id}" has ${field}, which a poster does not take`);
                }
            }
        }
        else {
            for (const field of POSTER_FIELDS) {
                if (object[field] !== undefined) {
                    add(`/objects/${id}/${field}`, `"${id}" has ${field}, which only a poster takes`);
                }
            }
        }
    }
    const posterCount = Object.values(lesson.objects ?? {}).filter((object) => isPosterKind(object.kind)).length;
    if (posterCount > POSTER_LIMITS.perLesson) {
        add('/objects', `${posterCount} posters; a lesson may have at most ${POSTER_LIMITS.perLesson}`);
    }
    const shapeCount = Object.values(lesson.objects ?? {}).filter((object) => isShapeKind(object.kind)).length;
    if (lessonGlow.size > SHAPE_LIMITS.glowColoursPerLesson) {
        add('/objects', `${lessonGlow.size} glow colours; a lesson may have at most ${SHAPE_LIMITS.glowColoursPerLesson}`);
    }
    if (shapeCount > SHAPE_LIMITS.shapesPerLesson) {
        add('/objects', `${shapeCount} shape objects; a lesson may have at most ${SHAPE_LIMITS.shapesPerLesson}`);
    }
    const seenStepIds = new Set();
    lesson.steps.forEach((step, index) => {
        const at = `/steps/${index}`;
        if (seenStepIds.has(step.id)) {
            // Results are keyed by step id, so duplicates would merge two different
            // questions into one column of the CSV export.
            add(`${at}/id`, `duplicate step id "${step.id}"`);
        }
        seenStepIds.add(step.id);
        if (step.focus !== undefined)
            requireSubject(step.focus, `${at}/focus`, 'focus');
        if (step.view !== undefined) {
            if ('look' in step.view && typeof step.view.look === 'string') {
                requireSubject(step.view.look, `${at}/view/look`, 'view.look');
            }
            if ('station' in step.view) {
                requireObject(step.view.station, `${at}/view/station`, 'view.station');
            }
        }
        step.hotspots?.forEach((hotspot, h) => {
            // A direction hotspot (`find`, `toward`) names no object; panos.ts judges which one it names.
            if (hotspot.object !== undefined) {
                requireObject(hotspot.object, `${at}/hotspots/${h}/object`, 'hotspot.object');
            }
        });
        if (step.quiz && step.quiz.answer >= step.quiz.options.length) {
            add(`${at}/quiz/answer`, `answer ${step.quiz.answer} is out of range for ${step.quiz.options.length} options`);
        }
        if (step.predict?.answer !== undefined && step.predict.answer >= step.predict.options.length) {
            add(`${at}/predict/answer`, `answer ${step.predict.answer} is out of range for ${step.predict.options.length} options`);
        }
        if (step.task) {
            // measure and assemble happen in the real world; furnishing in the villa (ADR-037).
            const needs = step.task.type === 'furnish' ? 'vr' : 'ar';
            if (lesson.mode !== needs) {
                add(`${at}/task`, `a "${step.task.type}" task needs ${needs === 'ar' ? 'an ar' : 'a vr'} lesson, but mode is "${lesson.mode}"`);
            }
            // Narrowed before anything is read off it: `task` is a union now, and
            // only a measure task has edges (ADR-025).
            const measuring = step.task.type === 'measure' ? step.task : undefined;
            if (step.task.type === 'assemble') {
                const placed = new Set(Object.keys(lesson.objects ?? {}));
                const seen = new Set();
                for (const part of step.task.parts) {
                    // A part with nowhere to go is a bone a child can never put right:
                    // where it belongs is its `pos`, and it has none.
                    if (!placed.has(part)) {
                        add(`${at}/task/parts`, `"${part}" is not an object this lesson places`);
                    }
                    // The same bone twice is a lesson that cannot be finished: there is
                    // one place for it, so one of the two can never be filled.
                    if (seen.has(part)) {
                        add(`${at}/task/parts`, `"${part}" is named twice`);
                    }
                    seen.add(part);
                }
            }
            const edges = measuring?.edges;
            if (edges) {
                for (let i = 1; i < edges.length; i++) {
                    if (edges[i] <= edges[i - 1]) {
                        add(`${at}/task/edges`, 'edges must be in ascending order');
                        break;
                    }
                }
            }
            if (step.predict) {
                const expected = step.predict.options.length - 1;
                if (!edges || edges.length !== expected) {
                    add(`${at}/task/edges`, `${step.predict.options.length} predict options need exactly ${expected} edges, got ${edges?.length ?? 0}`);
                }
            }
        }
    });
    // Showing, hiding, moving and highlighting (motion.ts): the same rules the
    // lesson generator's "Bewegingen kloppen" check says in Dutch.
    if (motion)
        for (const problem of assessMotion(lesson))
            add(problem.path, problem.en);
    // A world's features and their states (worlds.ts): the same rules the
    // lesson generator's "Wereld klopt" check says in Dutch.
    if (world && !unknownWorld)
        for (const problem of assessWorldFeatures(lesson, worlds))
            add(problem.path, problem.en);
    // A lesson's 360° photos and direction hotspots (panos.ts): the same rules
    // the lesson generator's "360°-foto klopt" check says in Dutch.
    if (pano)
        for (const problem of assessPanos(lesson))
            add(problem.path, problem.en);
    // A furnish task and the villa's rules (furniture.ts, ADR-037).
    if (furnish && !unknownWorld)
        for (const problem of assessFurnishing(lesson, worlds))
            add(problem.path, problem.en);
    // Library models (models.ts, ADR-043): where they stand, what they do and,
    // given the library's facts, that they exist and keep their distance.
    if (models) {
        const problems = assessModels(lesson, catalogue);
        for (const problem of problems)
            add(problem.path, problem.en);
        // Distances only where every model is known: an unknown slug has no size.
        if (catalogue && !problems.some((problem) => problem.kind === 'unknown-model')) {
            for (const problem of assessModelDistance(lesson, catalogue, worlds))
                add(problem.path, problem.en);
        }
    }
    return errors;
}
/**
 * Validates a parsed lesson document. Structural checks come from the JSON
 * Schema; the cross-field rules it cannot express are applied afterwards, and
 * only when the structure already holds (otherwise the semantic pass would be
 * reading fields whose types it cannot trust).
 */
export function validateLesson(document, options = {}) {
    if (!validateStructure(document)) {
        return { valid: false, errors: (validateStructure.errors ?? []).map(fromAjv) };
    }
    const errors = semanticErrors(document, options.motion !== false, options.world !== false, options.pano !== false, options.furnish !== false, options.models !== false, options.catalogue, options.worlds, options.requireKnownWorld === true);
    return errors.length > 0 ? { valid: false, errors } : { valid: true, lesson: document };
}
/** Convenience for CLI and test use: parses JSON text, then validates it. */
export function validateLessonJson(text) {
    let parsed;
    try {
        parsed = JSON.parse(text);
    }
    catch (error) {
        return {
            valid: false,
            errors: [{ path: '/', message: `not valid JSON: ${error.message}` }],
        };
    }
    return validateLesson(parsed);
}
//# sourceMappingURL=validate.js.map